import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { Icon, type IconSize } from "./Icon";
import { useSurface } from "./Surface";

/**
 * Classes of `Badge`: `variant` is the treatment, `tone` the palette color feeding it (`info` renders as `primary`);
 * `rule` is `md` with roomier height (the option group "Obrigatório" pill); `inherit` keeps the parent font size, `flush` also adds no vertical padding so it sits on the parent line height;
 * `elevation="card"` lifts a badge laid over a photo; `opaque` drops the 85% see-through of `solid inverse` (the item price chip over a light photo).
 */
export const badgeVariants = cva("inline-flex w-fit shrink-0 items-center gap-1 whitespace-nowrap", {
  variants: {
    variant: { solid: "", soft: "", outline: "border" },
    tone: {
      neutral: "",
      primary: "",
      info: "",
      secondary: "",
      success: "",
      warning: "",
      danger: "",
      inverse: "",
    },
    size: {
      "2xs": "px-1 py-px text-3xs",
      xs: "px-1.5 py-0.5 text-2xs",
      sm: "px-2 py-0.5 text-xs",
      md: "px-2.5 py-1 text-sm",
      lg: "px-2.5 py-1 text-base",
      rule: "px-2 py-1.5 text-sm",
      inherit: "px-1.5 py-0.5",
      flush: "px-2",
    },
    shape: { rounded: "rounded-badge", pill: "rounded-full" },
    uppercase: { true: "uppercase tracking-wide", false: "" },
    weight: { normal: "font-normal", medium: "font-medium", semibold: "font-semibold", bold: "font-bold" },
    elevation: { none: "", card: "shadow-card" },
    opaque: { true: "", false: "" },
  },
  compoundVariants: [
    { variant: "solid", tone: "neutral", class: "bg-gray-scale-500 text-on-media" },
    { variant: "solid", tone: ["primary", "info"], class: "bg-primary text-primary-foreground" },
    { variant: "solid", tone: "secondary", class: "bg-secondary text-secondary-foreground" },
    { variant: "solid", tone: "success", class: "bg-success text-on-media" },
    { variant: "solid", tone: "warning", class: "bg-warning text-warning-foreground" },
    { variant: "solid", tone: "danger", class: "bg-danger text-danger-foreground" },
    { variant: "solid", tone: "inverse", class: "bg-gray-scale-900/85 text-on-media" },
    { variant: "solid", tone: "inverse", opaque: true, class: "bg-gray-scale-900" },

    { variant: "soft", tone: "neutral", class: "bg-card-text/10 text-card-text/80" },
    { variant: "soft", tone: ["primary", "info"], class: "bg-primary/10 text-primary" },
    { variant: "soft", tone: "secondary", class: "bg-secondary/10 text-secondary" },
    { variant: "soft", tone: "success", class: "bg-success/10 text-success" },
    { variant: "soft", tone: "warning", class: "bg-warning/10 text-warning" },
    { variant: "soft", tone: "danger", class: "bg-danger/10 text-danger" },
    { variant: "soft", tone: "inverse", class: "bg-on-media/20 text-on-media" },

    { variant: "outline", tone: "neutral", class: "border-gray-scale-300 text-card-text" },
    { variant: "outline", tone: ["primary", "info"], class: "border-primary text-primary" },
    { variant: "outline", tone: "secondary", class: "border-secondary text-secondary" },
    { variant: "outline", tone: "success", class: "border-success text-success" },
    { variant: "outline", tone: "warning", class: "border-warning text-warning" },
    { variant: "outline", tone: "danger", class: "border-danger text-danger" },
    { variant: "outline", tone: "inverse", class: "border-on-media text-on-media" },
  ],
  defaultVariants: { variant: "soft", tone: "neutral", size: "sm", shape: "rounded", uppercase: false, weight: "bold", elevation: "none", opaque: false },
});

const BADGE_ICON_SIZE: Record<NonNullable<VariantProps<typeof badgeVariants>["size"]>, IconSize> = {
  "2xs": "2xs",
  xs: "2xs",
  sm: "xs",
  md: "sm",
  lg: "md",
  rule: "sm",
  inherit: "xs",
  flush: "xs",
};

/** Neutral soft/outline ink on the page surface; the base recipe is drawn for card surfaces. */
const NEUTRAL_ON_PAGE: Partial<Record<NonNullable<VariantProps<typeof badgeVariants>["variant"]>, string>> = {
  soft: "bg-page-text/10 text-page-text/80",
  outline: "text-page-text",
};

/** Props of `Badge`; `icon` renders a leading glyph sized to the badge. */
export type BadgeProps = VariantProps<typeof badgeVariants> &
  HTMLAttributes<HTMLSpanElement> & {
    icon?: IconGlyph;
  };

/** Short status or promo label (tone x variant x size x shape x weight); neutral ink follows the surface; children may be a `Price`. */
export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant, tone, size, shape, uppercase, weight, elevation, opaque, icon, className, children, ...rest },
  ref,
) {
  const surface = useSurface();
  const neutralOnPage = surface === "page" && (tone ?? "neutral") === "neutral" ? NEUTRAL_ON_PAGE[variant ?? "soft"] : undefined;
  return (
    <span
      {...rest}
      ref={ref}
      className={cn(badgeVariants({ variant, tone, size, shape, uppercase, weight, elevation, opaque }), neutralOnPage, className)}
    >
      {icon && <Icon as={icon} size={BADGE_ICON_SIZE[size ?? "sm"]} />}
      {children}
    </span>
  );
});

/** Classes of `CountBadge`: a round numeric overlay. */
export const countBadgeVariants = cva(
  "inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 font-bold leading-none tabular-nums",
  {
    variants: {
      size: { xs: "text-xs", sm: "text-sm" },
      tone: {
        primary: "bg-primary text-primary-foreground",
        "on-primary": "bg-primary-foreground text-primary",
        danger: "bg-danger text-danger-foreground",
        neutral: "bg-gray-scale-900 text-on-media",
      },
      bordered: { true: "ring-2 ring-card", false: "" },
    },
    defaultVariants: { size: "xs", tone: "primary", bordered: false },
  },
);

/** Props of `CountBadge`; counts above `max` render as "max+"; `size` is the digit size inside the fixed 20px bubble. */
export type CountBadgeProps = VariantProps<typeof countBadgeVariants> &
  Omit<HTMLAttributes<HTMLSpanElement>, "children"> & {
    count: number;
    max?: number;
    label?: string;
  };

/** Numeric bubble (cart count, unread); `label` gives screen readers the full meaning. */
export const CountBadge = forwardRef<HTMLSpanElement, CountBadgeProps>(function CountBadge(
  { count, max = 99, label, size, tone, bordered, className, ...rest },
  ref,
) {
  return (
    <span
      {...rest}
      ref={ref}
      aria-label={label}
      role={label ? "status" : undefined}
      className={cn(countBadgeVariants({ size, tone, bordered }), className)}
    >
      {count > max ? `${max}+` : count}
    </span>
  );
});
